import { supabase } from '../client';

export interface CalendarEventRow {
  id: string;
  org_id: string;
  user_id: string;
  title: string;
  description: string | null;
  location: string | null;
  all_day: boolean;
  starts_at: string;
  ends_at: string;
  reminder_offsets_minutes: number[];
  created_at: string;
  updated_at: string;
}

export interface CalendarReminderRow {
  id: string;
  event_id: string;
  org_id: string;
  user_id: string;
  fire_at: string;
  offset_minutes: number;
  acknowledged_at: string | null;
  created_at: string;
}

export interface DueCalendarReminder extends CalendarReminderRow {
  event_title: string;
  event_starts_at: string;
}

const EVENTS = 'ggj_b2b_calendar_events';
const REMINDERS = 'ggj_b2b_calendar_event_reminders';

function uniqueSortedOffsets(offsets: number[]): number[] {
  const s = new Set(
    offsets
      .map((n) => Math.floor(Number(n)))
      .filter((n) => Number.isFinite(n) && n >= 0 && n <= 10080)
  );
  return Array.from(s).sort((a, b) => a - b);
}

/** Build reminder rows: fire_at = starts_at - offset minutes; drop fires already in the past. */
export function buildReminderInserts(
  eventId: string,
  orgId: string,
  userId: string,
  startsAtIso: string,
  offsets: number[]
): Omit<CalendarReminderRow, 'id' | 'acknowledged_at' | 'created_at'>[] {
  const startMs = new Date(startsAtIso).getTime();
  if (!Number.isFinite(startMs)) return [];
  const now = Date.now();
  const uniq = uniqueSortedOffsets(offsets);
  const out: Omit<CalendarReminderRow, 'id' | 'acknowledged_at' | 'created_at'>[] = [];
  for (const offset of uniq) {
    const fireMs = startMs - offset * 60_000;
    if (fireMs <= now) continue;
    out.push({
      event_id: eventId,
      org_id: orgId,
      user_id: userId,
      fire_at: new Date(fireMs).toISOString(),
      offset_minutes: offset,
    });
  }
  return out;
}

export const calendarEventsService = {
  async listInRange(orgId: string, userId: string, rangeStart: string, rangeEnd: string): Promise<CalendarEventRow[]> {
    const { data, error } = await supabase
      .from(EVENTS)
      .select('*')
      .eq('org_id', orgId)
      .eq('user_id', userId)
      .lt('starts_at', rangeEnd)
      .gt('ends_at', rangeStart)
      .order('starts_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as CalendarEventRow[];
  },

  async getById(orgId: string, userId: string, id: string): Promise<CalendarEventRow | null> {
    const { data, error } = await supabase
      .from(EVENTS)
      .select('*')
      .eq('org_id', orgId)
      .eq('user_id', userId)
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return (data as CalendarEventRow) ?? null;
  },

  async create(
    orgId: string,
    userId: string,
    payload: {
      title: string;
      description?: string | null;
      location?: string | null;
      all_day: boolean;
      starts_at: string;
      ends_at: string;
      reminder_offsets_minutes: number[];
    }
  ): Promise<CalendarEventRow> {
    const offsets = uniqueSortedOffsets(payload.reminder_offsets_minutes);
    const { data: row, error } = await supabase
      .from(EVENTS)
      .insert({
        org_id: orgId,
        user_id: userId,
        title: payload.title.trim(),
        description: payload.description?.trim() || null,
        location: payload.location?.trim() || null,
        all_day: payload.all_day,
        starts_at: payload.starts_at,
        ends_at: payload.ends_at,
        reminder_offsets_minutes: offsets,
      })
      .select('*')
      .single();
    if (error) throw error;
    const ev = row as CalendarEventRow;
    await this.syncReminders(orgId, userId, ev.id, ev.starts_at, ev.reminder_offsets_minutes);
    return ev;
  },

  async update(
    orgId: string,
    userId: string,
    id: string,
    payload: Partial<{
      title: string;
      description: string | null;
      location: string | null;
      all_day: boolean;
      starts_at: string;
      ends_at: string;
      reminder_offsets_minutes: number[];
    }>
  ): Promise<CalendarEventRow> {
    const patch: Record<string, unknown> = {};
    if (payload.title !== undefined) patch.title = payload.title.trim();
    if (payload.description !== undefined) patch.description = payload.description?.trim() || null;
    if (payload.location !== undefined) patch.location = payload.location?.trim() || null;
    if (payload.all_day !== undefined) patch.all_day = payload.all_day;
    if (payload.starts_at !== undefined) patch.starts_at = payload.starts_at;
    if (payload.ends_at !== undefined) patch.ends_at = payload.ends_at;
    if (payload.reminder_offsets_minutes !== undefined) {
      patch.reminder_offsets_minutes = uniqueSortedOffsets(payload.reminder_offsets_minutes);
    }
    const { data: row, error } = await supabase.from(EVENTS).update(patch).eq('id', id).eq('org_id', orgId).eq('user_id', userId).select('*').single();
    if (error) throw error;
    const ev = row as CalendarEventRow;
    if (
      payload.starts_at !== undefined ||
      payload.reminder_offsets_minutes !== undefined
    ) {
      await this.syncReminders(orgId, userId, ev.id, ev.starts_at, ev.reminder_offsets_minutes);
    }
    return ev;
  },

  async delete(orgId: string, userId: string, id: string): Promise<void> {
    const { error } = await supabase.from(EVENTS).delete().eq('id', id).eq('org_id', orgId).eq('user_id', userId);
    if (error) throw error;
  },

  async syncReminders(orgId: string, userId: string, eventId: string, startsAt: string, offsets: number[]): Promise<void> {
    await supabase.from(REMINDERS).delete().eq('event_id', eventId).eq('org_id', orgId).eq('user_id', userId);
    const rows = buildReminderInserts(eventId, orgId, userId, startsAt, offsets);
    if (rows.length === 0) return;
    const { error } = await supabase.from(REMINDERS).insert(rows);
    if (error) throw error;
  },

  /** Reminders that should surface to the user (fired, not yet acknowledged). */
  async listDueReminders(orgId: string, userId: string): Promise<DueCalendarReminder[]> {
    const now = new Date().toISOString();
    const { data: rem, error } = await supabase
      .from(REMINDERS)
      .select('*')
      .eq('org_id', orgId)
      .eq('user_id', userId)
      .is('acknowledged_at', null)
      .lte('fire_at', now)
      .order('fire_at', { ascending: true });
    if (error) throw error;
    const list = (rem ?? []) as CalendarReminderRow[];
    if (list.length === 0) return [];
    const eventIds = [...new Set(list.map((r) => r.event_id))];
    const { data: evs, error: e2 } = await supabase
      .from(EVENTS)
      .select('id,title,starts_at')
      .eq('org_id', orgId)
      .eq('user_id', userId)
      .in('id', eventIds);
    if (e2) throw e2;
    const titleMap = new Map((evs ?? []).map((e: { id: string; title: string; starts_at: string }) => [e.id, e]));
    return list.map((r) => {
      const ev = titleMap.get(r.event_id);
      return {
        ...r,
        event_title: ev?.title ?? '(Event)',
        event_starts_at: ev?.starts_at ?? r.fire_at,
      };
    });
  },

  async acknowledgeReminder(orgId: string, userId: string, reminderId: string): Promise<void> {
    const { error } = await supabase
      .from(REMINDERS)
      .update({ acknowledged_at: new Date().toISOString() })
      .eq('id', reminderId)
      .eq('org_id', orgId)
      .eq('user_id', userId);
    if (error) throw error;
  },
};
